import { Controller } from 'react-hook-form';
import {
  ButtonPrimary,
  ButtonPseudo,
  Checkbox,
  IconClose,
  IconSearch,
  InputText,
  Popup,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { EditSidepanelTypes } from './types';
import BacklightText from './backlight-text';

import cn from './styles.module.css';

const EditSidepanel = ({
  editPanelIsOpen,
  setDirtyPopupVisible,
  isDirty,
  handleClose,
  globalError,
  editPanelType,
  editKey,
  editIsLoading,
  onSubmit,
  searchText,
  onSearchLanguages,
  setValue,
  allAvailableLanguages,
  fields,
  control,
  filteredLanguagesList,
  dirtyPopupVisible,
}: EditSidepanelTypes) => (
  <>
    <SidePanel
      type={SidePanelType.Customers}
      closeOnClickOutside
      isOpen={editPanelIsOpen}
      onClose={() => {
        if (isDirty) {
          setDirtyPopupVisible(true);
        } else {
          handleClose();
        }
      }}
      errorSidepanel={globalError}
      title={`${editPanelType?.charAt(0).toUpperCase()}${editPanelType?.slice(
        1,
      )} ${
        editKey?.includes('NotAvailable') ? 'not available' : 'available'
      } languages`}
      sidepanelExpandedKey="edit-languages-panel"
      footer={
        <>
          <ButtonPrimary
            text={editIsLoading ? 'Loading...' : 'Save'}
            onClick={onSubmit}
            disabled={editIsLoading || !isDirty}
            bold
          />
          <ButtonPseudo
            text="Close"
            onClick={() => {
              if (isDirty) {
                setDirtyPopupVisible(true);
              } else {
                handleClose();
              }
            }}
            bold
          />
        </>
      }
    >
      <>
        <InputText
          name="searchLanguage"
          labelClassName={cn.searchLanguageInput}
          prefix={<IconSearch />}
          prefixClassName={cn.searchLanguageInputIcon}
          value={searchText}
          placeholder="Language"
          onChange={(e) => onSearchLanguages(e.target.value)}
          suffix={
            <button
              type="button"
              className={`${cn.searchInputClearButton} ${
                !searchText ? cn.hidden : ''
              }`}
              onClick={() => onSearchLanguages('')}
            >
              <IconClose />
            </button>
          }
          suffixClassName={cn.searchInputSuffix}
        />
        {!searchText && (
          <div className={cn.selectAllWrapper}>
            <Checkbox
              name="selectAll"
              type="checkbox"
              onChange={(e) => {
                if (editKey) {
                  if (e.target.checked) {
                    setValue(
                      editKey,
                      allAvailableLanguages?.map(
                        (item) => item.value as string,
                      ) as string[],
                    );
                  } else {
                    setValue(editKey, []);
                  }
                }
              }}
              checked={Boolean(
                editKey &&
                fields[editKey].length === allAvailableLanguages?.length,
              )}
              partialChecked={Boolean(
                editKey &&
                fields[editKey] &&
                fields[editKey]?.length &&
                fields[editKey].length !== allAvailableLanguages?.length,
              )}
              title="Select all"
            />
          </div>
        )}

        {editKey && (
          <div className={cn.languages}>
            <Controller
              control={control}
              name={editKey}
              render={({ field: { value, name, onChange } }) => (
                <div className={cn.languagesList}>
                  {filteredLanguagesList?.map((item) => (
                    <div className={cn.sidepanelLanguageItem} key={item.value}>
                      <Checkbox
                        name={`${name}-${item.name}`}
                        type="checkbox"
                        labelClassName={cn.languageItemLabel}
                        title={
                          <div className={cn.languageName}>
                            <BacklightText
                              text={item.name}
                              searchQuery={searchText || ''}
                              className={cn.languageShortName}
                            />
                            <BacklightText
                              text={item.suffix as string}
                              searchQuery={searchText || ''}
                              className={cn.languageLongName}
                            />
                          </div>
                        }
                        checked={value?.includes(item.value as string)}
                        onChange={() => {
                          let nextValues = ([] as string[]).concat(value);

                          if (nextValues.includes(item.value as string)) {
                            nextValues = nextValues?.filter(
                              (itemValue) => itemValue !== item.value,
                            );
                          } else {
                            nextValues.push(item.value as string);
                          }

                          onChange(nextValues);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            />
          </div>
        )}
      </>
    </SidePanel>
    <Popup
      title="You have unsaved changes"
      description=""
      onCancelClick={() => setDirtyPopupVisible(false)}
      onOkClick={handleClose}
      cancelButtonText="Cancel"
      okButtonText="Close"
      isVisible={dirtyPopupVisible}
      parent={document.getElementById('dirty-popup') as HTMLElement}
    />
  </>
);

export default EditSidepanel;
